// Pure validation / payload shaping extracted from TrainingTrackBuilder.tsx
// so the save rules are unit-testable without the page UI.

export interface TrainingTrackFormLike {
  trackName: string;
  items: unknown[];
  trackType?: 'kb' | 'playbook';
  /** The real form carries more fields; these are the ones validation reads. */
  playbookConfig: { enabled: boolean; industry?: string; roleName?: string };
  /** Present on a Playbook Live track: the AI customer and what it raises. */
  personaConfig?: {
    aiCustomerRole?: string;
    buyingObjective?: string;
    agendaItems?: unknown[];
  } | null;
}

/**
 * Validate a training-track form before save.
 * Returns a user-facing error message, or null when the form is valid.
 */
export function validateTrainingTrackForm(formData: TrainingTrackFormLike): string | null {
  if (!formData.trackName.trim()) {
    return 'Track name is required';
  }
  // Every playbook track runs on a sales playbook; it is not optional.
  if (formData.trackType === 'playbook'
      && (!formData.playbookConfig.industry || !formData.playbookConfig.roleName)) {
    return 'Choose the sales playbook and customer role for this track';
  }
  // A Playbook Live track carries no items[]: its content is the AI customer
  // and the knowledge base items it raises. Every playbook track runs on
  // Playbook Live, so the AI customer is required.
  const persona = formData.personaConfig;
  if (formData.trackType === 'playbook' && !persona) {
    return 'Describe the customer, then build them, before saving';
  }
  if (persona) {
    if (!persona.aiCustomerRole?.trim()) {
      return 'Describe the customer, then build them, before saving';
    }
    if (!persona.buyingObjective?.trim()) {
      return 'The customer needs something they are trying to buy';
    }
    if (!persona.agendaItems?.length) {
      return 'The customer needs at least one concern to raise';
    }
    return null;
  }

  if (formData.items.length === 0) {
    return 'Please add at least one knowledge base item';
  }
  return null;
}

/**
 * Shape the save payload: strip contentSnapshot from items (the backend
 * fetches fresh content at session start) and set the publish status.
 */
export function buildTrainingTrackPayload<
  TItem extends { contentSnapshot?: unknown },
  TForm extends { items: TItem[] }
>(
  formData: TForm,
  publish: boolean
): Omit<TForm, 'items'> & { items: Omit<TItem, 'contentSnapshot'>[]; status: 'active' | 'draft' } {
  const itemsWithoutSnapshot = formData.items.map(({ contentSnapshot, ...item }) => item);
  return {
    ...formData,
    items: itemsWithoutSnapshot,
    status: publish ? 'active' : 'draft',
  };
}
